/**
 * Filters above list and board: Search, Client, Who, Category, Queue, Priority,
 * Status, Due date, Blocked — then one-click working views and removable chips.
 *
 * Same URL facets for both views. Category is the catalogue's service; Queue is the
 * explicit queue field, so a service and a queue can be combined. The server narrows
 * by both (`board.ts` `facetsToTaskFilters`); the menus are read from the server too,
 * never from the rows on screen. Widths live on `.tfilters` in portal.css.
 */

import { TASK_PRIORITY_MAX, TASK_PRIORITY_MIN, TASK_QUEUES } from '@fareast/shared'
import { useId } from 'react'

import { Button, Seg, SegItem } from '../../../components/portal/Button.js'
import { Field, Label } from '../../../components/portal/Form.js'
import { QUEUE_WORD, STATUS_WORD, priorityWord } from '../../../components/studio/TaskCardFull.js'
import {
  STUDIO_COLUMN,
  WORKING_VIEWS,
  activeFilterChips,
  activeWorkingView,
  type ListBlocked,
  type ListFacets,
  type ListPriority,
  type ListStatus,
  type ListWhen,
  type WorkingViewId,
} from './board.js'

const WHEN_OPTIONS: readonly { readonly value: ListWhen; readonly label: string }[] = [
  { value: 'all', label: 'Any date' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'week', label: 'Due this week' },
  { value: 'undated', label: 'No date' },
]

const BLOCKED_OPTIONS: readonly { readonly value: ListBlocked; readonly label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

const STATUS_OPTIONS: readonly { readonly value: ListStatus; readonly label: string }[] = [
  { value: 'all', label: 'Any status' },
  { value: 'open', label: STATUS_WORD.open },
  { value: 'claimed', label: STATUS_WORD.claimed },
  { value: 'in_progress', label: STATUS_WORD.in_progress },
  { value: 'blocked', label: STATUS_WORD.blocked },
  { value: 'review', label: 'Needs review' },
  { value: 'done', label: STATUS_WORD.done },
]

/* A `<select>` speaks strings; the facet is a number (`ListPriority` says why). */
const PRIORITY_OPTIONS: readonly { readonly value: string; readonly label: string }[] = [
  { value: 'all', label: 'Any priority' },
  ...Array.from({ length: TASK_PRIORITY_MAX - TASK_PRIORITY_MIN + 1 }, (_, i) => {
    const n = (TASK_PRIORITY_MIN + i) as 1 | 2 | 3 | 4 | 5
    return { value: String(n), label: priorityWord(n) }
  }),
]

export const CLIENT_ALL = 'all'
export const CLIENT_STUDIO = 'studio'
export const WHO_ALL = 'all'
export const WHO_NONE = 'none'
export const BLOCKED_ALL = 'all'
export const CATEGORY_ALL = 'all'
export const QUEUE_ALL = 'all'
export const PRIORITY_ALL = 'all'
export const STATUS_ALL = 'all'
export const SEARCH_EMPTY = ''

export type TaskFiltersProps = {
  readonly facets: ListFacets
  readonly onFacet: (patch: Partial<ListFacets>) => void
  readonly onWorkingView: (id: WorkingViewId) => void
  readonly onClearAll: () => void
  /** The Client menu: every client with work in hand (`tasks_summary`'s columns). */
  readonly clients: readonly { readonly client: string | null; readonly name: string }[]
  /** The Who menu: everybody a task can be given to (`tasks_list_assignees`). */
  readonly assignees: readonly { readonly id: string; readonly name: string }[]
  /** The Category menu: the catalogue's services. */
  readonly services: readonly { readonly code: string; readonly name: string }[]
  /** The signed-in studio person — always offered as **Me** in Who, even with no tasks. */
  readonly me: { readonly id: string; readonly name: string } | null
}

export function TaskFilters({
  facets,
  onFacet,
  onWorkingView,
  onClearAll,
  clients: clientColumns,
  assignees,
  services,
  me,
}: TaskFiltersProps) {
  const clients = clientOptions(clientColumns)
  const people = whoOptions(assignees, me)
  const categories = categoryOptions(services)
  const active = activeWorkingView(facets, me?.id ?? null)
  const chips = activeFilterChips(facets, {
    client: (value) =>
      value === CLIENT_STUDIO
        ? STUDIO_COLUMN
        : (clients.find((one) => one.value === value)?.label ?? value),
    who: (value) =>
      value === WHO_NONE
        ? 'Unassigned'
        : me !== null && value === me.id
          ? 'Me'
          : (people.find((one) => one.value === value)?.label ?? value),
    category: (value) => categories.find((one) => one.value === value)?.label ?? value,
    queue: (value) => (value in QUEUE_WORD ? QUEUE_WORD[value as keyof typeof QUEUE_WORD] : value),
  })

  return (
    <div className="tfilters">
      <SearchField
        value={facets.q}
        onChange={(q) => {
          onFacet({ q })
        }}
      />
      <FilterSelect
        label="Client"
        value={facets.client}
        onChange={(client) => {
          onFacet({ client })
        }}
        options={[
          { value: CLIENT_ALL, label: 'All clients' },
          { value: CLIENT_STUDIO, label: STUDIO_COLUMN },
          ...clients,
        ]}
      />
      <FilterSelect
        label="Who"
        value={facets.who}
        onChange={(who) => {
          onFacet({ who })
        }}
        options={[
          { value: WHO_ALL, label: 'Anyone' },
          { value: WHO_NONE, label: 'Unassigned' },
          ...people,
        ]}
      />
      <FilterSelect
        label="Category"
        value={facets.category}
        onChange={(category) => {
          onFacet({ category })
        }}
        options={[{ value: CATEGORY_ALL, label: 'All categories' }, ...categories]}
      />
      <FilterSelect
        label="Queue"
        value={facets.queue}
        onChange={(queue) => {
          onFacet({ queue })
        }}
        options={[
          { value: QUEUE_ALL, label: 'All queues' },
          ...TASK_QUEUES.map((queue) => ({ value: queue, label: QUEUE_WORD[queue] })),
        ]}
      />
      <FilterSelect
        label="Priority"
        value={String(facets.priority)}
        onChange={(priority) => {
          onFacet({
            priority: priority === PRIORITY_ALL ? PRIORITY_ALL : (Number(priority) as ListPriority),
          })
        }}
        options={[...PRIORITY_OPTIONS]}
      />
      <FilterSelect
        label="Status"
        value={facets.status}
        onChange={(status) => {
          onFacet({ status: status as ListStatus })
        }}
        options={[...STATUS_OPTIONS]}
      />
      <FilterSelect
        label="Due date"
        value={facets.when}
        onChange={(when) => {
          onFacet({ when: when as ListWhen })
        }}
        options={[...WHEN_OPTIONS]}
      />
      <FilterSelect
        label="Blocked on client"
        value={facets.blocked}
        onChange={(blocked) => {
          onFacet({ blocked: blocked as ListBlocked })
        }}
        options={[...BLOCKED_OPTIONS]}
      />

      <div className="tfilters__views">
        <Seg label="Working views">
          {WORKING_VIEWS.map((view) => (
            <SegItem
              key={view.id}
              on={active === view.id}
              onClick={() => {
                onWorkingView(view.id)
              }}
            >
              {view.label}
            </SegItem>
          ))}
        </Seg>
      </div>

      {chips.length === 0 ? null : (
        <div className="tfilters__chips" role="group" aria-label="Active filters">
          {chips.map((chip) => (
            <Button
              key={chip.key}
              size="sm"
              variant="ghost"
              onClick={() => {
                onFacet({ [chip.key]: LIST_DEFAULT_FOR[chip.key] })
              }}
            >
              {chip.label} ×
            </Button>
          ))}
          <Button size="sm" onClick={onClearAll}>
            Clear all
          </Button>
        </div>
      )}
    </div>
  )
}

/** Per-key default so a chip click clears only that facet. */
const LIST_DEFAULT_FOR: { readonly [K in keyof ListFacets]: ListFacets[K] } = {
  client: CLIENT_ALL,
  who: WHO_ALL,
  when: 'all',
  blocked: BLOCKED_ALL,
  category: CATEGORY_ALL,
  q: SEARCH_EMPTY,
  priority: PRIORITY_ALL,
  status: STATUS_ALL,
  queue: QUEUE_ALL,
}

function SearchField({
  value,
  onChange,
}: {
  readonly value: string
  readonly onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <div className="tfilters__search">
      <Field>
        <Label htmlFor={id}>Search</Label>
        <input
          className="input"
          id={id}
          type="search"
          value={value}
          placeholder="Title, note, ref…"
          data-tasks-search
          onChange={(event) => {
            onChange(event.target.value)
          }}
        />
      </Field>
    </div>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  readonly label: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly options: readonly { readonly value: string; readonly label: string }[]
}) {
  const id = useId()
  /*
   * The value the address names, offered even when the menu's source does not list
   * it (a client whose work is all done, somebody no longer assignable): a select
   * with no option for its value shows its first option instead, and would say "All
   * clients" above a list narrowed to one.
   */
  const shown = options.some((one) => one.value === value)
    ? options
    : [...options, { value, label: value }]
  return (
    <Field>
      <Label htmlFor={id}>{label}</Label>
      <select
        className="select"
        id={id}
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      >
        {shown.map((one) => (
          <option key={one.value} value={one.value}>
            {one.label}
          </option>
        ))}
      </select>
    </Field>
  )
}

/**
 * Every client with work in hand, by company name.
 *
 * From `tasks_summary` asked with nothing (the page's `whole`), not from the rows on
 * screen: the page loads fifty at a time, and a client whose tasks are not among them
 * would otherwise be one nobody can pick (session 42).
 */
function clientOptions(
  clients: readonly { readonly client: string | null; readonly name: string }[],
): readonly { readonly value: string; readonly label: string }[] {
  return clients
    .flatMap((one) => (one.client === null ? [] : [{ value: one.client, label: one.name }]))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
}

/**
 * Everybody a task can be given to, from `tasks_list_assignees`, the directory the
 * Who cell already reads (session 42): the rows on screen are a page, not the studio.
 */
function whoOptions(
  assignees: readonly { readonly id: string; readonly name: string }[],
  me: { readonly id: string; readonly name: string } | null,
): readonly { readonly value: string; readonly label: string }[] {
  const seen = new Map<string, string>()
  for (const one of assignees) seen.set(one.id, one.name)
  /*
   * Always offer **Me**, even when the signed-in person has no live task — otherwise
   * Who cannot answer "my work" on an empty day, which is the filter's whole point.
   */
  if (me !== null) seen.set(me.id, 'Me')

  const others = [...seen.entries()]
    .filter(([id]) => me === null || id !== me.id)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))

  if (me === null) return others
  return [{ value: me.id, label: 'Me' }, ...others]
}

/**
 * The catalogue's services.
 *
 * Every one of them rather than those on the board (session 42): the rows on screen
 * are a page, so "the services on the board" is no longer something the page knows.
 *
 * Services only. The seven queue words are the Queue select's, beside it: offered
 * here as well, the same board could be narrowed two ways from two controls, and
 * choosing a queue in Category quietly meant "and no service". A `q:<queue>` category
 * in an old link still works (`facetsToTaskFilters`); nothing offers one any more.
 */
function categoryOptions(
  services: readonly { readonly code: string; readonly name: string }[],
): readonly { readonly value: string; readonly label: string }[] {
  return services
    .map((service) => ({ value: `s:${service.code}`, label: service.name }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
}
